import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Plus, Search, Pencil, Trash2, Upload, Database, History, FilePlus2, FileX2, FilePen,
  Tags, BarChart3, FileSpreadsheet, FileDown, Lock,
} from 'lucide-react';
import api, { downloadUrl } from '../lib/api';
import { cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CURRENCY_OPTIONS } from '../lib/currency';
import { NumberStepper } from '../components/NumberStepper';
import { fmtRelative } from '../lib/format';
import { EmptyState } from '../components/common';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from '../components/ui/sheet';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `id-${Math.random().toString(36).slice(2)}-${Date.now()}`);

// 8 master types mirroring the Cost BOQ sections — P3 canonical order.
const MTYPES = ['material', 'manpower', 'supervisors', 'vehicle', 'tools', 'subcontractor', 'safety', 'misc'];
const TRADE_TYPES = new Set(['manpower', 'supervisors']); // use a "Trade" field, no category
const fieldsFor = (t) => TRADE_TYPES.has(t)
  ? [['trade', 'Trade'], ['unit', 'Unit'], ['rate', 'Rate (AED)'], ['currency', 'Currency'], ['effectiveDate', 'Effective Date']]
  : [['description', 'Description'], ['category', 'Category'], ['unit', 'Unit'], ['rate', 'Rate (AED)'], ['currency', 'Currency'], ['effectiveDate', 'Effective Date']];
const columnsFor = (t) => TRADE_TYPES.has(t)
  ? [['code', 'Code'], ['trade', 'Trade'], ['unit', 'Unit'], ['rate', 'Rate (AED)'], ['currency', 'Currency']]
  : [['code', 'Code'], ['description', 'Description'], ['category', 'Category'], ['unit', 'Unit'], ['rate', 'Rate (AED)'], ['currency', 'Currency']];
// Editable fields per master type (code is system-generated & read-only).
const FIELDS = Object.fromEntries(MTYPES.map((t) => [t, fieldsFor(t)]));
// Table columns (code shown read-only first).
const COLUMNS = Object.fromEntries(MTYPES.map((t) => [t, columnsFor(t)]));
const PREFIX = { material: 'MAT', vehicle: 'VEH', manpower: 'MNP', supervisors: 'SUP', tools: 'TLS', safety: 'SAF', subcontractor: 'SBC', misc: 'MSC' };
// Shared column sizing + alignment so all three tabs render uniformly.
// `description`/`trade` are left without a fixed width so they absorb slack
// (prevents ragged gaps); short columns get consistent fixed widths.
const COL_META = {
  code: { w: 'w-36', right: false },
  description: { w: '', right: false },
  trade: { w: '', right: false },
  category: { w: 'w-48', right: false },
  unit: { w: 'w-24', right: false },
  rate: { w: 'w-40', right: true },
  currency: { w: 'w-28', right: false },
};
const TYPE_LABEL = { material: 'Material', vehicle: 'Vehicle', manpower: 'Manpower', supervisors: 'Supervisors', tools: 'Tools & Equipment', safety: 'Safety', subcontractor: 'Subcontractor', misc: 'Misc' };
// P1 — categories offered in the (authoritative) form dropdown. Trade masters
// (MANPOWER/SUPERVISORS) are managed only from their own tabs, so they are never
// offered here — picking a category routes the rate into that category's collection.
const ROUTABLE_CATEGORIES = ['MATERIAL', 'VEHICLE', 'TOOLS & EQUIPMENT', 'SUBCONTRACTOR', 'SAFETY', 'MISC'];
const CATEGORY_TAB = { MATERIAL: 'material', VEHICLE: 'vehicle', 'TOOLS & EQUIPMENT': 'tools', SAFETY: 'safety', SUBCONTRACTOR: 'subcontractor', MISC: 'misc' };
const DEFAULT_CATEGORY = {
  material: 'MATERIAL', vehicle: 'VEHICLE', manpower: 'MANPOWER', supervisors: 'SUPERVISORS',
  tools: 'TOOLS & EQUIPMENT', safety: 'SAFETY', subcontractor: 'SUBCONTRACTOR', misc: 'MISC',
};
const ACTION_META = {
  create: { icon: FilePlus2, tone: 'text-[hsl(var(--success))]', label: 'Created' },
  update: { icon: FilePen, tone: 'text-[hsl(var(--teal))]', label: 'Updated' },
  delete: { icon: FileX2, tone: 'text-[hsl(var(--danger))]', label: 'Deleted' },
};

export default function Masters() {
  const { can } = useAuth();
  const canEdit = can('rate_masters', 'readwrite');
  const [type, setType] = useState('material');
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive
  const [categoryFilter, setCategoryFilter] = useState('all'); // all | <distinct category>
  const [cur, setCur] = useState('AED');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [usage, setUsage] = useState(0);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditRows, setAuditRows] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [reportType, setReportType] = useState('all');
  const [reportStatus, setReportStatus] = useState('all');

  const load = useCallback(async (t) => {
    const { data } = await api.get(`/masters/${t}`);
    setRows(data);
  }, []);
  useEffect(() => { load(type); }, [type, load]);
  useEffect(() => { api.get('/settings').then((r) => setCur((r.data?.currency || 'AED').toUpperCase())).catch(() => {}); }, []);

  const rateLbl = (key, lbl) => (key === 'rate' ? `Rate (${cur})` : lbl);

  const openAudit = async () => {
    setAuditOpen(true); setAuditRows(null);
    try { const { data } = await api.get('/master-audit', { params: { mtype: type } }); setAuditRows(data); }
    catch { setAuditRows([]); }
  };

  const loadReport = useCallback(async () => {
    setReport(null);
    try {
      const params = {};
      if (reportType !== 'all') params.mtype = reportType;
      if (reportStatus !== 'all') params.status = reportStatus;
      const { data } = await api.get('/masters/report', { params });
      setReport(data);
    } catch { setReport({ rows: [], totals: {}, error: true }); }
  }, [reportType, reportStatus]);

  const openReport = () => { setReportOpen(true); };
  useEffect(() => { if (reportOpen) loadReport(); }, [reportOpen, loadReport]);

  const reportQuery = () => {
    const p = [];
    if (reportType !== 'all') p.push(`mtype=${reportType}`);
    if (reportStatus !== 'all') p.push(`status=${reportStatus}`);
    return p.length ? `?${p.join('&')}` : '';
  };

  const openNew = () => {
    setEditing(null);
    setForm({ active: true, category: DEFAULT_CATEGORY[type], currency: 'AED', effectiveDate: new Date().toISOString().slice(0, 10), brands: [] });
    setUsage(0); setSheetOpen(true);
  };
  const openEdit = async (row) => {
    setEditing(row); setForm({ currency: 'AED', brands: [], ...row }); setSheetOpen(true);
    try { const { data } = await api.get(`/masters/${type}/usage/${row.code}`); setUsage(data.count); } catch { setUsage(0); }
  };

  const renderCell = (key, row) => {
    if (key === 'rate') return Number(row[key] || 0).toLocaleString('en-AE', { minimumFractionDigits: 2 });
    if (key === 'currency') return row.currency || 'AED';
    return row[key];
  };

  const save = async () => {
    try {
      const payload = { ...form };
      // Coerce brand rates to numbers; drop empty brand rows.
      if (Array.isArray(payload.brands)) {
        payload.brands = payload.brands
          .filter((b) => (b.name || '').trim())
          .map((b) => ({ ...b, id: b.id || uid(), rate: Number(b.rate) || 0, active: b.active !== false }));
      }
      if (editing) {
        const { data } = await api.put(`/masters/${type}/${editing.id}`, payload);
        const targetTab = CATEGORY_TAB[(data?.category || '').toUpperCase()];
        toast.success(targetTab && targetTab !== type ? `Moved to ${TYPE_LABEL[targetTab]} (${data.code})` : 'Updated');
        setSheetOpen(false);
        if (targetTab && targetTab !== type) setType(targetTab); else load(type);
      } else {
        const { data } = await api.post(`/masters/${type}`, payload);
        const targetTab = CATEGORY_TAB[(data?.category || '').toUpperCase()];
        toast.success(targetTab && targetTab !== type ? `Saved under ${TYPE_LABEL[targetTab]} (${data.code})` : 'Rate created');
        setSheetOpen(false);
        if (targetTab && targetTab !== type) setType(targetTab); else load(type);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const toggleActive = async (row, next) => {
    try {
      await api.put(`/masters/${type}/${row.id}`, { active: next });
      setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, active: next } : r)));
      toast.success(`${row.code} ${next ? 'activated' : 'deactivated'}`);
    } catch { toast.error('Could not update status'); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.code}? Consider deactivating instead to preserve history.`)) return;
    await api.delete(`/masters/${type}/${row.id}`); toast.success('Deleted'); load(type);
  };

  const doImport = async () => {
    try {
      await api.post(`/masters/${type}/import`, { csv: importText });
      toast.success('Import complete'); setImportOpen(false); setImportText(''); load(type);
    } catch { toast.error('Import failed — check CSV headers'); }
  };

  // ---- Brand editor helpers ----
  const addBrand = () => setForm((f) => ({
    ...f,
    brands: [...(f.brands || []), { id: uid(), name: '', rate: '', currency: f.currency || 'AED', effectiveDate: new Date().toISOString().slice(0, 10), remark: '', active: true }],
  }));
  const updateBrand = (idx, field, val) => setForm((f) => {
    const brands = [...(f.brands || [])];
    brands[idx] = { ...brands[idx], [field]: val };
    return { ...f, brands };
  });
  const removeBrand = (idx) => setForm((f) => ({ ...f, brands: (f.brands || []).filter((_, i) => i !== idx) }));

  const cols = COLUMNS[type];
  const fields = FIELDS[type];
  // Distinct, non-empty categories from the current tab's loaded data (dynamic).
  const categoryOptions = Array.from(
    new Set(rows.map((r) => (r.category || '').trim()).filter(Boolean)),
  ).sort((a, b) => a.localeCompare(b));
  const filtered = rows.filter((r) => {
    const active = r.active !== false;
    if (statusFilter === 'active' && !active) return false;
    if (statusFilter === 'inactive' && active) return false;
    if (categoryFilter !== 'all' && (r.category || '').trim() !== categoryFilter) return false;
    return Object.values(r).join(' ').toLowerCase().includes(q.toLowerCase());
  });
  const filtersActive = q || statusFilter !== 'all' || categoryFilter !== 'all';

  return (
    <div className="space-y-6 p-4 md:p-6 lg:p-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold md:text-3xl">Rate Masters</h1>
          <p className="text-sm text-muted-foreground">Rates across all 8 Cost BOQ sections that drive cost build-ups.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" data-testid="masters-report-button" onClick={openReport}><BarChart3 size={15} className="mr-2" />Category Report</Button>
          {canEdit && (
            <>
              <Button variant="outline" data-testid="masters-audit-button" onClick={openAudit}><History size={15} className="mr-2" />Audit Log</Button>
              <Button variant="outline" data-testid="masters-import-button" onClick={() => setImportOpen(true)}><Upload size={15} className="mr-2" />Import CSV</Button>
              <Button data-testid="masters-add-button" onClick={openNew}><Plus size={15} className="mr-2" />Add Rate</Button>
            </>
          )}
        </div>
      </div>

      <Tabs value={type} onValueChange={(v) => { setType(v); setCategoryFilter('all'); }}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="-mx-4 max-w-full overflow-x-auto px-4 no-scrollbar sm:mx-0 sm:px-0">
            <TabsList className="w-max">
              {MTYPES.map((t) => (
                <TabsTrigger key={t} value={t} data-testid={`masters-tab-${t}`}>{TYPE_LABEL[t]}</TabsTrigger>
              ))}
            </TabsList>
          </div>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px]" data-testid="masters-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="active">Active only</SelectItem>
                <SelectItem value="inactive">Inactive only</SelectItem>
              </SelectContent>
            </Select>
            {categoryOptions.length > 0 && (
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[160px]" data-testid="masters-category-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categoryOptions.map((c) => <SelectItem key={c} value={c} data-testid={`masters-category-option-${c}`}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <div className="relative max-w-xs flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search rates…" className="pl-9" data-testid="masters-search" />
            </div>
          </div>
        </div>

        {MTYPES.map((t) => (
          <TabsContent key={t} value={t} className="mt-4">
            {filtered.length === 0 ? (
              <EmptyState
                testid={`masters-empty-${t}`}
                icon={Database}
                title={filtersActive ? 'No matching rates' : `No ${t} rates yet`}
                description={filtersActive
                  ? 'Try a different search term, status or category filter to see all rates.'
                  : `Add ${t} rates manually or import a CSV to power your cost build-ups.`}
                action={canEdit && !filtersActive ? (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setImportOpen(true)}><Upload size={15} className="mr-2" />Import CSV</Button>
                    <Button onClick={openNew}><Plus size={15} className="mr-2" />Add Rate</Button>
                  </div>
                ) : null}
                compact
              />
            ) : (
            <div className="erak-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid={`masters-table-${t}`}>
                  <thead><tr className="border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                    {cols.map(([key, lbl]) => {
                      const m = COL_META[key] || {};
                      return <th key={key} className={cn('px-4 py-3 font-semibold', m.w, m.right ? 'text-right' : 'text-left')}>{rateLbl(key, lbl)}</th>;
                    })}
                    <th className="w-40 px-4 py-3 text-left font-semibold">Status</th>
                    <th className="w-24 px-4 py-3 text-right font-semibold">Actions</th>
                  </tr></thead>
                  <tbody>
                    {filtered.map((row) => {
                      const active = row.active !== false;
                      const brandCount = (row.brands || []).length;
                      return (
                      <tr key={row.id} className={`border-b last:border-0 hover:bg-muted/40 ${active ? '' : 'opacity-55'}`} data-testid={`master-row-${row.code}`}>
                        {cols.map(([key]) => {
                          const m = COL_META[key] || {};
                          return (
                          <td key={key} className={cn('px-4 py-2.5 align-middle', m.w,
                            key === 'rate' ? 'money' : '',
                            key === 'code' ? 'font-num text-[hsl(var(--copper))]' : '',
                            key === 'currency' ? 'font-num text-xs text-muted-foreground' : '')}>
                            <div className={cn('flex items-center gap-2', m.right && 'justify-end')}>
                              {renderCell(key, row)}
                              {key === 'description' && t === 'material' && brandCount > 0 && (
                                <Badge variant="secondary" className="gap-1 text-[10px]" data-testid={`master-brands-badge-${row.code}`}><Tags size={10} />{brandCount} brand{brandCount > 1 ? 's' : ''}</Badge>
                              )}
                            </div>
                          </td>
                          );
                        })}
                        <td className="w-40 px-4 py-2.5 align-middle">
                          {canEdit ? (
                            <div className="flex items-center gap-2">
                              <Switch checked={active} onCheckedChange={(v) => toggleActive(row, v)} data-testid={`master-active-toggle-${row.code}`} aria-label="Active status" />
                              <span className={`text-[11px] font-medium ${active ? 'text-[hsl(var(--success))]' : 'text-muted-foreground'}`}>{active ? 'Active' : 'Inactive'}</span>
                            </div>
                          ) : (
                            <Badge variant={active ? 'default' : 'secondary'} className="text-[10px]">{active ? 'Active' : 'Inactive'}</Badge>
                          )}
                        </td>
                        <td className="w-24 px-4 py-2.5 align-middle text-right">
                          {canEdit && (
                            <div className="flex justify-end gap-1">
                              <button onClick={() => openEdit(row)} className="rounded-lg p-1.5 hover:bg-muted" data-testid={`master-edit-${row.code}`}><Pencil size={14} /></button>
                              <button onClick={() => remove(row)} className="rounded-lg p-1.5 text-[hsl(var(--danger))] hover:bg-muted" data-testid={`master-delete-${row.code}`}><Trash2 size={14} /></button>
                            </div>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Add/Edit slide-over */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent data-testid="master-edit-sheet" className="flex w-full flex-col overflow-y-auto sm:max-w-md">
          <SheetHeader><SheetTitle>{editing ? `Edit ${editing.code}` : `Add ${TYPE_LABEL[type]} Rate`}</SheetTitle></SheetHeader>
          {editing && usage > 0 && (
            <div className="mt-4 rounded-xl border border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.12)] p-3 text-xs text-[hsl(var(--warning))]">
              Used in {usage} live tender{usage > 1 ? 's' : ''} — changes affect new revisions only.
            </div>
          )}
          <div className="mt-6 space-y-4">
            {/* System code (read-only) */}
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Lock size={12} />Code <span className="text-muted-foreground">(system-generated)</span></Label>
              <Input readOnly disabled data-testid="master-code-readonly"
                value={editing ? editing.code : `Auto · next ${PREFIX[type]}-…`}
                className="font-num text-muted-foreground" />
            </div>
            {fields.map(([key, lbl]) => (
              <div key={key} className="space-y-1.5">
                <Label>{rateLbl(key, lbl)}</Label>
                {key === 'currency' ? (
                  <Select value={(form.currency || 'AED').toUpperCase()} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger data-testid="master-currency-select" className="font-num"><SelectValue placeholder="Currency" /></SelectTrigger>
                    <SelectContent>{CURRENCY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                ) : key === 'category' ? (
                  <Select value={form.category || DEFAULT_CATEGORY[type]} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="master-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      {ROUTABLE_CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`master-category-opt-${c}`}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : key === 'effectiveDate' ? (
                  <Input type="date" data-testid="master-effective-date" value={form.effectiveDate || ''} onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })} className="font-num" />
                ) : key === 'rate' ? (
                  <NumberStepper value={form.rate ?? 0} step={1} decimals={2} min={0} size="md" className="w-full" data-testid="master-field-rate"
                    onChange={(v) => setForm({ ...form, rate: v })} />
                ) : (
                  <Input type="text" value={form[key] ?? ''}
                    data-testid={`master-field-${key}`}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    className="" />
                )}
              </div>
            ))}

            {/* Active toggle */}
            <div className="flex items-center justify-between rounded-xl border bg-muted/30 px-3 py-2.5">
              <div>
                <div className="text-sm font-medium">Active</div>
                <div className="text-[11px] text-muted-foreground">Inactive rates are hidden from pickers but kept for history.</div>
              </div>
              <Switch checked={form.active !== false} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="master-form-active-toggle" />
            </div>

            {/* Material Brands editor */}
            {type === 'material' && (
              <div className="rounded-xl border p-3" data-testid="master-brands-editor">
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-semibold"><Tags size={15} className="text-[hsl(var(--copper))]" />Brand Options</div>
                  <Button type="button" size="sm" variant="outline" className="h-7" onClick={addBrand} data-testid="master-add-brand"><Plus size={13} className="mr-1" />Add brand</Button>
                </div>
                <p className="mb-2 text-[11px] text-muted-foreground">Optional brand/price alternatives for this material. Estimators can pick a brand on a cost line; the brand rate overrides the base rate.</p>
                {(form.brands || []).length === 0 ? (
                  <div className="rounded-lg border border-dashed py-4 text-center text-[11px] text-muted-foreground">No brands yet. The base rate ({cur} {Number(form.rate || 0).toLocaleString()}) will be used.</div>
                ) : (
                  <div className="space-y-2">
                    {(form.brands || []).map((b, idx) => (
                      <div key={b.id || idx} className="rounded-lg border bg-muted/20 p-2.5" data-testid={`brand-row-${idx}`}>
                        <div className="flex items-center gap-2">
                          <Input placeholder="Brand name" value={b.name || ''} onChange={(e) => updateBrand(idx, 'name', e.target.value)} className="h-8 flex-1" data-testid={`brand-name-${idx}`} />
                          <Input type="number" placeholder="Rate" value={b.rate ?? ''} onChange={(e) => updateBrand(idx, 'rate', e.target.value)} className="h-8 w-24 font-num text-right" data-testid={`brand-rate-${idx}`} />
                          <button type="button" onClick={() => removeBrand(idx)} className="rounded p-1.5 text-[hsl(var(--danger))] hover:bg-muted" data-testid={`brand-remove-${idx}`}><Trash2 size={14} /></button>
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <Input placeholder="Remark (optional)" value={b.remark || ''} onChange={(e) => updateBrand(idx, 'remark', e.target.value)} className="h-7 flex-1 text-xs" data-testid={`brand-remark-${idx}`} />
                          <div className="flex items-center gap-1.5">
                            <Switch checked={b.active !== false} onCheckedChange={(v) => updateBrand(idx, 'active', v)} data-testid={`brand-active-${idx}`} />
                            <span className="text-[10px] text-muted-foreground">{b.active !== false ? 'Active' : 'Off'}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <SheetFooter className="mt-8">
            <Button variant="outline" onClick={() => setSheetOpen(false)}>Cancel</Button>
            <Button data-testid="master-save-button" onClick={save}>Save</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Import {type} rates (CSV)</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Paste CSV with headers. Leave <span className="font-num">code</span> blank to auto-generate ({PREFIX[type]}-xxx). Existing codes are updated.</p>
          {!['manpower', 'supervisors'].includes(type) && (
            <p className="text-xs text-muted-foreground">Category is forced to <span className="font-medium">{DEFAULT_CATEGORY[type]}</span> for this tab — any other or unknown category in the CSV is mapped to it.</p>
          )}
          <Textarea rows={8} value={importText} onChange={(e) => setImportText(e.target.value)} className="font-num text-xs"
            placeholder={`code,description,category,unit,rate\n,Sample item,${DEFAULT_CATEGORY[type] || 'MISC'},nos,1250`} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
            <Button onClick={doImport}>Import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rate Master audit log — read only */}
      <Sheet open={auditOpen} onOpenChange={setAuditOpen}>
        <SheetContent data-testid="masters-audit-sheet" className="flex w-full flex-col sm:max-w-lg">
          <SheetHeader><SheetTitle className="flex items-center gap-2"><History size={16} />{type} rate audit log</SheetTitle></SheetHeader>
          <div className="mt-4 flex-1 space-y-2 overflow-y-auto pr-1">
            {auditRows === null ? (
              <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-muted/40" />)}</div>
            ) : auditRows.length === 0 ? (
              <EmptyState testid="masters-audit-empty" icon={History} title="No changes recorded yet"
                description="Every add, edit and delete to this rate master will be logged here with the old and new values." compact />
            ) : auditRows.map((ev) => {
              const meta = ACTION_META[ev.action] || ACTION_META.update;
              const Icon = meta.icon;
              return (
                <div key={ev.id} className="rounded-xl border p-3" data-testid={`audit-row-${ev.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Icon size={14} className={meta.tone} />
                      <span className={`text-xs font-semibold ${meta.tone}`}>{meta.label}</span>
                      <span className="font-num text-xs text-[hsl(var(--copper))]">{ev.code || '—'}</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground">{fmtRelative(ev.createdAt)}</span>
                  </div>
                  <div className="mt-1 text-[10px] text-muted-foreground">by {ev.actor}</div>
                  {(ev.changes || []).length > 0 && (
                    <div className="mt-2 space-y-0.5">
                      {ev.changes.map((c, i) => (
                        <div key={`${c.field}-${i}`} className="flex items-center gap-1.5 text-[11px]">
                          <span className="text-muted-foreground">{c.field}:</span>
                          <span className="font-num line-through opacity-60">{c.old === null || c.old === undefined || c.old === '' ? '∅' : String(c.old)}</span>
                          <span className="text-muted-foreground">→</span>
                          <span className="font-num font-medium">{c.new === null || c.new === undefined || c.new === '' ? '∅' : String(c.new)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>

      {/* Rate Master Category Report (Part D) */}
      <Sheet open={reportOpen} onOpenChange={setReportOpen}>
        <SheetContent data-testid="masters-report-sheet" className="flex w-full flex-col sm:max-w-2xl">
          <SheetHeader><SheetTitle className="flex items-center gap-2"><BarChart3 size={16} />Rate Master Category Report</SheetTitle></SheetHeader>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger className="w-[150px]" data-testid="report-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {MTYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_LABEL[t]}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={reportStatus} onValueChange={setReportStatus}>
              <SelectTrigger className="w-[140px]" data-testid="report-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="active">Active only</SelectItem>
                <SelectItem value="inactive">Inactive only</SelectItem>
              </SelectContent>
            </Select>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" data-testid="report-export-csv" onClick={() => window.open(downloadUrl(`/masters/report.csv${reportQuery()}`), '_blank')}><FileDown size={14} className="mr-1.5" />CSV</Button>
              <Button variant="outline" size="sm" data-testid="report-export-xlsx" onClick={() => window.open(downloadUrl(`/masters/report.xlsx${reportQuery()}`), '_blank')}><FileSpreadsheet size={14} className="mr-1.5" />Excel</Button>
            </div>
          </div>

          {/* KPI strip */}
          {report && !report.error && (
            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                ['Categories', report.totals.categoryCount],
                ['Items', report.totals.itemCount],
                ['Active', report.totals.activeCount],
                ['Brands', report.totals.brandCount],
              ].map(([lbl, val]) => (
                <div key={lbl} className="rounded-xl border bg-muted/20 p-2.5 text-center">
                  <div className="font-num text-lg font-bold">{val ?? 0}</div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{lbl}</div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 flex-1 overflow-y-auto">
            {report === null ? (
              <div className="space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-muted/40" />)}</div>
            ) : report.error || report.rows.length === 0 ? (
              <EmptyState testid="report-empty" icon={BarChart3} title="No data for this filter"
                description="Adjust the type or status filters to see category rate statistics." compact />
            ) : (
              <table className="w-full text-sm" data-testid="report-table">
                <thead><tr className="border-b bg-muted/40 text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 font-semibold">Type</th>
                  <th className="px-3 py-2 font-semibold">Category</th>
                  <th className="px-3 py-2 text-right font-semibold">Items</th>
                  <th className="px-3 py-2 text-right font-semibold">Active</th>
                  <th className="px-3 py-2 text-right font-semibold">Min</th>
                  <th className="px-3 py-2 text-right font-semibold">Avg</th>
                  <th className="px-3 py-2 text-right font-semibold">Max</th>
                </tr></thead>
                <tbody>
                  {report.rows.map((g, i) => (
                    <tr key={`${g.mtype}-${g.category}-${i}`} className="border-b last:border-0 hover:bg-muted/30" data-testid={`report-row-${g.mtype}-${g.category}`}>
                      <td className="px-3 py-2"><Badge variant="secondary" className="text-[10px]">{TYPE_LABEL[g.mtype] || g.mtype}</Badge></td>
                      <td className="px-3 py-2">
                        {g.category}
                        {g.brandCount > 0 && <span className="ml-1.5 text-[10px] text-muted-foreground">· {g.brandCount} brand{g.brandCount > 1 ? 's' : ''}</span>}
                        {g.inactiveCount > 0 && <span className="ml-1.5 text-[10px] text-[hsl(var(--warning))]">· {g.inactiveCount} inactive</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-num">{g.itemCount}</td>
                      <td className="px-3 py-2 text-right font-num">{g.activeCount}</td>
                      <td className="px-3 py-2 text-right font-num money">{g.minRate.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-num money">{g.avgRate.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-num money">{g.maxRate.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
